<template>
  <RsModal
    :id="id"
    :model-value="modelValue"
    title="Account Settings"
    size="lg"
    lazy
    secondary-button="Manage Cookies"
    :disabled="isSaveDisabled"
    @on-secondary-button-clicked="handleManageCookies"
    @input="onChangeModalVisible"
    @shown="resetForm"
    @submit="handleSubmit"
  >
    <VeeForm ref="userForm" as="div">
      <div class="row">
        <div class="col-3">
          <RsAvatar
            image="/img/default-user.png"
            class="w-100 h-100"
          />
        </div>
        <div class="col-9">
          <RsFormInput
            :value="storeAccount.email"
            label="Email"
            name="Emails"
            type="text"
            disabled
            required
            :rules="{ required: true, email: true }"
          />
          <div class="row row-collapse">
            <div class="col-6">
              <RsFormInput
                v-model.trim="account.firstName"
                label="First Name"
                name="First Name"
                type="text"
                required
                :lazy="false"
                :rules="{ required: true }"
              />
            </div>
            <div class="col-6">
              <RsFormInput
                v-model.trim="account.lastName"
                label="Last Name"
                name="Last Name"
                type="text"
                required
                :lazy="false"
                :rules="{ required: true }"
              />
            </div>
            <!-- Change Password Button -->
            <div class="col-12">
              <RsButton
                id="change-password"
                variant="secondary"
                icon="fas fa-key mr-2"
                @click="handleChangePassword"
              >
                Change Password
              </RsButton>
            </div>
          </div>
        </div>
      </div>
    </VeeForm>
    <RsPartialAccountMfa :email="storeAccount.email" />
  </RsModal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Form as VeeForm, defineRule } from 'vee-validate';
import { required, email as emailRule, min, is } from '@vee-validate/rules';
import { isEqual } from 'lodash-es';
import { ICurrentUserFormWithPasswords } from '@/shared';
import { mapModuleActions, mapModuleGetters } from '@/shared/vuex-toolkit';
import { users } from '@/store/typed/typed-modules';
import { forgotPassword } from '@/shared/services/users';
import RsPartialAccountMfa from '@/components/RsPartialAccountMfa.vue';

defineRule('email', emailRule);
defineRule('required', required);
defineRule('min', min);
defineRule('is', is);

export default defineComponent({
  name: 'RsModalAccount',
  components: {
    VeeForm,
    RsPartialAccountMfa,
  },
  props: {
    id: {
      type: [Number, String],
      default: 'account_modal',
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    tabIndex: {
      type: Number,
      default: 0,
    },
  },
  emits: ['input', 'update:modelValue'],
  data() {
    return {
      account: {
        firstName: '',
        lastName: '',
        hash: '',
      } as ICurrentUserFormWithPasswords,
      confirmedPassword: '',
      strength: 0,
      strengthColor: '',
      strengthText: '',
    };
  },

  computed: {
    ...mapModuleGetters(users, { storeAccount: 'account' }),

    isSaveDisabled(): boolean {
      const { firstName, lastName } = this.account;
      const accountForm = { firstName, lastName };
      // Remove email for the comparison
      const { email, ...namesStoreAccount } = this.storeAccount;
      return !firstName || !lastName || isEqual(accountForm, namesStoreAccount);
    },
  },

  methods: {
    ...mapModuleActions(users, ['saveSelf']),
    forgotPassword,

    onChangeModalVisible(value: boolean): void {
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
      this.resetForm();
    },

    handleChangePassword(): void {
      this.$rsGlobals.$rsSwal.info({
        title: 'Change Password?',
        text: 'We will send you an email with a link to set your new password.',
        confirmButtonText: 'Send Password Reset Email',
        confirmHandler: async () => {
          await this.forgotPassword(this.storeAccount.email);
          this.$eventBus.emit('show-success-toast', { title: 'Password reset email sent!', message: 'Please check your inbox.' });
        },
      });
    },

    handleManageCookies(): void {
      this.$emit('input', false);
      this.$emit('update:modelValue', false);
      window.location.hash = '#manage_cookies';
    },

    resetForm(): void {
      this.confirmedPassword = '';
      this.account = { ...this.storeAccount, hash: '' };
    },

    async handleSubmit(): Promise<void> {
      const { valid } = await (this.$refs.userForm as any).validate();
      if (!valid) {
        return;
      }

      const result = await this.$swal.fire({
        title: 'Password Required!',
        input: 'password',
        inputLabel: `Changing your information requires current password confirmation.`,
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off',
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      this.account.hash = result.value;
      await this.saveSelf(this.account);
      this.$eventBus.emit('show-success-toast', { message: 'Profile changes saved!' });
      this.onChangeModalVisible(false);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";
#enzuzo-cookies a {
  color: white;
}
.rs-avatar {
  width: 150px !important;
  height: 150px !important;
  display: block;
  margin: auto;
}
.strengthbar {
  width: 100%;
  height: 8px;
  margin-top: 10px;
  background-color: #eee;
  transition: width 500ms;
  border-radius: 15px;
}
.col-2{
  padding: 0px;
  p{
    margin-top: 5px;
  }
}
</style>
